import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, Send, Megaphone, Users, Eye } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import {
  sendTestSessionEmail,
  countBroadcastSegment,
} from "@/lib/email/test-session-email.functions";
import {
  previewBroadcastRecipients,
  type BroadcastRecipientPreview,
} from "@/lib/admin-broadcast-preview.functions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/admin/broadcast")({
  head: () => ({
    meta: [
      { title: "Broadcast · Admin · WeLivdIt" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminBroadcastPage,
});

type Segment = "enrolled" | "leads" | "all";
const SEGMENT_LABEL: Record<Segment, string> = {
  enrolled: "All Enrolled Paid Students",
  leads: "Signed-Up / Unenrolled Leads",
  all: "All Combined (Enrolled + Unenrolled Leads)",
};

function AdminBroadcastPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);

  const [testEmail, setTestEmail] = useState("sriramalla@welivdit.com");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [dateLabel, setDateLabel] = useState("");
  const [timeLabel, setTimeLabel] = useState("");
  const [gmeetUrl, setGmeetUrl] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [segment, setSegment] = useState<Segment>("enrolled");
  const [segmentCount, setSegmentCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);

  const send$ = useServerFn(sendTestSessionEmail);
  const count$ = useServerFn(countBroadcastSegment);
  const preview$ = useServerFn(previewBroadcastRecipients);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState<BroadcastRecipientPreview[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const [previewQuery, setPreviewQuery] = useState("");

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewQuery("");
    try {
      const r = await preview$({ data: { segment, limit: 500 } });
      setPreviewRecipients(r.recipients);
      setPreviewTotal(r.total);
      setPreviewTruncated(r.truncated);
    } catch (e) {
      toast.error("Couldn't load recipients", {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredPreview = previewRecipients.filter((r) => {
    if (!previewQuery.trim()) return true;
    const q = previewQuery.toLowerCase();
    return r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q);
  });

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const ok = data.session ? await hasAdminAccess(data.session) : false;
      if (!ok) {
        navigate({ to: "/auth" });
        return;
      }
      setAuthChecked(true);
    })();
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    setLoadingCount(true);
    setSegmentCount(null);
    count$({ data: { segment } })
      .then((r) => {
        if (!cancelled) setSegmentCount(r.count);
      })
      .catch(() => {
        if (!cancelled) setSegmentCount(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingCount(false);
      });
    return () => {
      cancelled = true;
    };
  }, [segment, authChecked]);

  const sendTest = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim())) {
      toast.error("Enter a valid test recipient email");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject / session title is required");
      return;
    }
    setSendingTest(true);
    try {
      const r = await send$({
        data: {
          email: testEmail.trim(),
          sessionTitle: subject,
          description: description || null,
          dateLabel: dateLabel || null,
          timeLabel: timeLabel || null,
          registrationUrl: gmeetUrl || null,
          isBroadcast: false,
        },
      });
      if (r.sent) toast.success(`Test email queued to ${testEmail}`);
      else toast.error("Send failed", { description: (r as any).reason ?? "unknown" });
    } catch (e: any) {
      toast.error("Send failed", { description: e?.message ?? String(e) });
    } finally {
      setSendingTest(false);
    }
  };

  const sendBroadcast = async () => {
    if (!subject.trim()) {
      toast.error("Subject / session title is required");
      return;
    }
    const confirmed = window.confirm(
      `Send this announcement to "${SEGMENT_LABEL[segment]}"${
        segmentCount != null ? ` (${segmentCount} recipients)` : ""
      } right now?`,
    );
    if (!confirmed) return;
    setSendingBroadcast(true);
    try {
      const r: any = await send$({
        data: {
          email: null,
          sessionTitle: subject,
          description: description || null,
          dateLabel: dateLabel || null,
          timeLabel: timeLabel || null,
          registrationUrl: gmeetUrl || null,
          isBroadcast: true,
          segment,
        },
      });
      if (r.sent) {
        toast.success(
          `Broadcast queued: ${r.queued}/${r.total} recipients${
            r.failed ? ` · ${r.failed} failed` : ""
          }`,
        );
      } else {
        toast.error("Broadcast failed", { description: `0/${r.total ?? 0} queued` });
      }
    } catch (e: any) {
      toast.error("Broadcast failed", { description: e?.message ?? String(e) });
    } finally {
      setSendingBroadcast(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const broadcastButtonLabel = () => {
    const base =
      segment === "leads"
        ? "Broadcast to Unenrolled Leads"
        : segment === "all"
          ? "Broadcast to All (Enrolled + Leads)"
          : "Broadcast to Enrolled Students";
    if (loadingCount) return `${base} (…)`;
    if (segmentCount == null) return base;
    return `${base} (${segmentCount} recipients)`;
  };

  return (
    <section className="mx-auto max-w-3xl px-5 py-10 space-y-8">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-primary" />
          <h1 className="font-display text-3xl font-semibold">Direct Broadcast</h1>
        </div>
        <p className="mt-1 text-sm text-foreground/70">
          Send an announcement to enrolled students, unenrolled leads, or everyone. Test first, broadcast in one click.
          Unsubscribed emails are automatically excluded.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm sm:col-span-2">
            <span className="text-xs font-medium text-foreground/70">Subject / Session title *</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Live Session Tonight · F-1 Interview Drill"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="text-xs font-medium text-foreground/70">Date (optional)</span>
            <input
              type="text"
              value={dateLabel}
              onChange={(e) => setDateLabel(e.target.value)}
              placeholder="29 Jul 2026"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm">
            <span className="text-xs font-medium text-foreground/70">Time (optional)</span>
            <input
              type="text"
              value={timeLabel}
              onChange={(e) => setTimeLabel(e.target.value)}
              placeholder="7:00 PM IST"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm sm:col-span-2">
            <span className="text-xs font-medium text-foreground/70">Google Meet link (optional)</span>
            <input
              type="url"
              value={gmeetUrl}
              onChange={(e) => setGmeetUrl(e.target.value)}
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <label className="text-sm sm:col-span-2">
            <span className="text-xs font-medium text-foreground/70">Message body</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="Write your announcement here..."
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <h2 className="font-semibold text-sm">Step 1 · Test to your inbox</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <label className="text-sm flex-1">
            <span className="text-xs font-medium text-foreground/70">Test recipient email</span>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={sendTest}
            disabled={sendingTest || sendingBroadcast}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-border bg-background text-sm font-semibold hover:bg-accent transition disabled:opacity-60"
          >
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test email
          </button>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-primary/40 bg-primary/[0.03] p-6 space-y-4">
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" /> Step 2 · 1-click broadcast
        </h2>

        <label className="block text-sm">
          <span className="text-xs font-medium text-foreground/70">Audience segment</span>
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value as Segment)}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="enrolled">All Enrolled Paid Students</option>
            <option value="leads">Signed-Up / Unenrolled Leads</option>
            <option value="all">All Combined (Enrolled + Unenrolled Leads)</option>
          </select>
          <span className="mt-1 block text-[11px] text-foreground/60">
            {loadingCount
              ? "Counting recipients…"
              : segmentCount != null
                ? `${segmentCount} unique recipients (unsubscribed emails excluded automatically)`
                : "Recipient count unavailable"}
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={sendBroadcast}
            disabled={sendingTest || sendingBroadcast}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
          >
            {sendingBroadcast ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />}
            {broadcastButtonLabel()}
          </button>
          <button
            type="button"
            onClick={openPreview}
            disabled={sendingBroadcast}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border bg-background text-sm font-semibold hover:bg-accent transition disabled:opacity-60"
          >
            <Eye className="h-4 w-4" /> Preview Recipients
          </button>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Recipients preview · {SEGMENT_LABEL[segment]}</DialogTitle>
            <DialogDescription>
              {previewLoading
                ? "Loading recipients…"
                : `${previewTotal} unique recipients${previewTruncated ? ` (showing first ${previewRecipients.length})` : ""}. Unsubscribed emails are excluded automatically.`}
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <input
              type="search"
              value={previewQuery}
              onChange={(e) => setPreviewQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto mt-3 border border-border rounded-lg">
            {previewLoading ? (
              <div className="p-8 flex items-center justify-center text-foreground/60 gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : filteredPreview.length === 0 ? (
              <div className="p-8 text-center text-sm text-foreground/60">No recipients match.</div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredPreview.map((r) => (
                  <li key={r.email} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-xs text-foreground/60 truncate">{r.email}</p>
                    </div>
                    <Badge variant={r.audience === "enrolled" ? "default" : "secondary"}>
                      {r.audience === "enrolled" ? "Enrolled" : "Lead"}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
